import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    TextField,
    Snackbar,
    Typography
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    ArrowForward as ForwardIcon,
    Save as SaveIcon,
    PlayArrow as PlayArrowIcon,
    AutoFixHigh as AutoFixHighIcon,
    Article as ArticleIcon
} from '@mui/icons-material';

import { api } from '../../utils/api';
import { useScraper } from '../../contexts/ScraperContext';
import { useEshopWizard } from '../../hooks/useEshopWizard';
import { useAdvancedSettings } from '../../hooks/useAdvancedSettings';
import EshopInfoStep from './EshopInfoStep';
import AuthSetupStep from './AuthSetupStep';
import SelectorConfigStep from './SelectorConfigStep';
import AdvancedSettingsStep from './AdvancedSettingsStep';
import ReviewStep from './ReviewStep';
import { extractErrorMessage, buildFullScraperConfig } from '../../utils/scraperConfig';

function EshopWizard({ onComplete, eshopId, eshopIdParam, onEshopNameChange }) {
    const wizardMountIdRef = useRef(`eshop-wizard-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const selectorConfigStepRef = useRef(null);
    const authSetupStepRef = useRef(null);

    useEffect(() => {
        console.debug('[EshopWizard] mounted', { mountId: wizardMountIdRef.current });
        return () => {
            console.debug('[EshopWizard] unmounted', { mountId: wizardMountIdRef.current });
        };
    }, []);

    const { scraperConfig, setScraperConfig, defaultScraperConfig } = useScraper();

    const effectiveEshopId = eshopId || eshopIdParam;

    // Reset scraper config whenever effectiveEshopId changes
    // This ensures data like conversions don't leak between different e-shops
    useEffect(() => {
        console.log('[DEBUG] EshopWizard - effectiveEshopId changed, resetting state to defaults', effectiveEshopId);
        setScraperConfig(JSON.parse(JSON.stringify(defaultScraperConfig)));
    }, [effectiveEshopId, setScraperConfig, defaultScraperConfig]);

    const {
        activeStep,
        eshopData,
        setEshopData,
        authConfig,
        setAuthConfig,
        advancedSettings,
        setAdvancedSettings,
        categories,
        setCategories,
        validationError,
        eshop,
        isEshopLoading,
        createEshopMutation,
        updateEshopMutation,
        saveEshopMutation,
        testConnectionMutation,
        testProductLoadingMutation,
        analyzeLoginMutation,
        handleNext,
        handleBack,
        jumpToStep,
        steps
    } = useEshopWizard({ onComplete, eshopId: effectiveEshopId });

    // Use the advanced settings hook
    const { setAdvancedSettings: hookSetAdvancedSettings } = useAdvancedSettings(eshop, effectiveEshopId);

    // Initialize advanced settings and categories when loaded from server
    useEffect(() => {
        console.log('[DEBUG] EshopWizard - eshop data changed:', eshop);
        console.log('[DEBUG] EshopWizard - eshopId:', effectiveEshopId);
        if (eshop && effectiveEshopId && eshop.config_json) {
            console.log('[DEBUG] EshopWizard - config_json:', eshop.config_json);
            if (eshop.config_json.advanced) {
                console.log('[DEBUG] Setting advanced settings:', eshop.config_json.advanced);
                hookSetAdvancedSettings(eshop.config_json.advanced);
            }

            // Initialize scraper config with values from the existing e-shop
            // This ensures URL fields and selectors are properly loaded
            const config = eshop.config_json;
            setScraperConfig(prevConfig => ({
                ...prevConfig,
                url: config.url || '',
                catalog_url: config.catalog_url || '',
                selectors: config.selectors || prevConfig.selectors,
                conversions: config.conversions || prevConfig.conversions || [],
                pagination: config.pagination || prevConfig.pagination,
                testing: config.testing || prevConfig.testing,
                load_detail_pages: config.load_detail_pages || false,
                enable_lazy_scroll: config.enable_lazy_scroll || false
            }));

            // Initialize categories if present in config
            if (config.categories && config.categories.length > 0) {
                setCategories(config.categories);
            }
        }
    }, [eshop, effectiveEshopId, hookSetAdvancedSettings, setScraperConfig, setCategories]);

    // Notify parent component when eshop name changes
    useEffect(() => {
        if (eshop && eshop.name && onEshopNameChange) {
            onEshopNameChange(eshop.name);
        }
    }, [eshop, onEshopNameChange]);

    // State for analysis and testing results
    const [analyzeLoginResult, setAnalyzeLoginResult] = useState(null);
    const [testConnectionResult, setTestConnectionResult] = useState(null);
    const [testProductLoadingResult, setTestProductLoadingResult] = useState(null);
    const [isTestingConnection, setIsTestingConnection] = useState(false);
    const [isTestingProductLoading, setIsTestingProductLoading] = useState(false);
    const [isAnalyzingLogin, setIsAnalyzingLogin] = useState(false);
    const [isAnalyzingSelectors, setIsAnalyzingSelectors] = useState(false);
    const [htmlSize, setHtmlSize] = useState(10000);
    const [iterations, setIterations] = useState(1);
    // No selectorConfigOpenPreview state needed anymore since SelectorConfigStep handles modal opening internally

    // State for save notifications
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Update auth config state when mutation succeeds
    useEffect(() => {
        if (analyzeLoginMutation.isSuccess) {
            const response = analyzeLoginMutation.data;
            const { selectors } = response.data;

            // Update form fields with suggested selectors
            const newAuthConfig = {
                ...authConfig,
                login_url: selectors.login_url || authConfig.login_url,
                username_field: selectors.username_field || authConfig.username_field,
                password_field: selectors.password_field || authConfig.password_field,
                login_button: selectors.login_button || authConfig.login_button
            };

            setAuthConfig(newAuthConfig);

            setAnalyzeLoginResult({
                success: true,
                message: response.data.message || "Login analysis completed successfully"
            });
        } else if (analyzeLoginMutation.isError) {
            setAnalyzeLoginResult({
                success: false,
                message: extractErrorMessage(analyzeLoginMutation.error, 'Login analysis failed')
            });
        }

        setIsAnalyzingLogin(analyzeLoginMutation.isLoading);
    }, [analyzeLoginMutation.isSuccess, analyzeLoginMutation.isError, analyzeLoginMutation.data, analyzeLoginMutation.error, analyzeLoginMutation.isLoading, authConfig, setAuthConfig]);

    // Handle test connection results
    useEffect(() => {
        if (testConnectionMutation.isSuccess) {
            setTestConnectionResult(testConnectionMutation.data.data);
        } else if (testConnectionMutation.isError) {
            setTestConnectionResult({
                success: false,
                message: extractErrorMessage(testConnectionMutation.error, 'Connection test failed')
            });
        }

        setIsTestingConnection(testConnectionMutation.isLoading);
    }, [testConnectionMutation.isSuccess, testConnectionMutation.isError, testConnectionMutation.data, testConnectionMutation.error, testConnectionMutation.isLoading]);

    // Handle test product loading results
    useEffect(() => {
        if (testProductLoadingMutation.isSuccess) {
            const resultPayload = testProductLoadingMutation.data.data;
            console.debug('[EshopWizard] testProductLoadingMutation success', {
                mountId: wizardMountIdRef.current,
                resultSnapshotHash: resultPayload ? JSON.stringify({
                    success: resultPayload.success,
                    message: resultPayload.message,
                    productsCount: resultPayload.products?.length || 0,
                    firstProductName: resultPayload.products?.[0]?.name || resultPayload.products?.[0]?.product_name || ''
                }) : null,
                timestamp: Date.now()
            });
            setTestProductLoadingResult(resultPayload);
        } else if (testProductLoadingMutation.isError) {
            setTestProductLoadingResult({
                success: false,
                message: extractErrorMessage(testProductLoadingMutation.error, 'Product loading test failed')
            });
        }

        setIsTestingProductLoading(testProductLoadingMutation.isLoading);
    }, [testProductLoadingMutation.isSuccess, testProductLoadingMutation.isError, testProductLoadingMutation.data, testProductLoadingMutation.error, testProductLoadingMutation.isLoading]);

    // Handle save notifications
    useEffect(() => {
        if (updateEshopMutation.isSuccess || saveEshopMutation.isSuccess) {
            setSnackbarMessage('E-shop saved successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } else if (updateEshopMutation.isError) {
            setSnackbarMessage(extractErrorMessage(updateEshopMutation.error, 'Failed to save e-shop'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } else if (saveEshopMutation.isError) {
            setSnackbarMessage(extractErrorMessage(saveEshopMutation.error, 'Failed to save e-shop'));
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    }, [updateEshopMutation.isSuccess, updateEshopMutation.isError, updateEshopMutation.error, saveEshopMutation.isSuccess, saveEshopMutation.isError, saveEshopMutation.error]);

    const handleTestProductLoading = () => {
        // Check if analysis is still running and wait for it to complete
        if (isAnalyzingLogin) {
            setTestProductLoadingResult({
                success: false,
                message: 'Login analysis is still in progress. Please wait for it to complete before testing product loading.'
            });
            setIsTestingProductLoading(false);
            return;
        }

        // Check if auth config has been set up properly with more robust validation (for authentication-dependent tests)
        if (!authConfig.login_url?.trim() || !authConfig.username_field?.trim() || !authConfig.password_field?.trim() || !authConfig.login_button?.trim()) {
            setTestProductLoadingResult({
                success: false,
                message: 'Authentication configuration is incomplete. Please run "Analyze Login" first or fill in all authentication fields manually. All fields must contain valid CSS selectors.'
            });
            setIsTestingProductLoading(false);
            return;
        }

        // Additional check: ensure test credentials are provided
        if (!authConfig.test_credentials?.username?.trim() || !authConfig.test_credentials?.password?.trim()) {
            setTestProductLoadingResult({
                success: false,
                message: 'Test credentials are required for authentication testing. Please enter a test username and password.'
            });
            setIsTestingProductLoading(false);
            return;
        }

        // For new e-shops, we need to create a temporary one first
        if (!effectiveEshopId) {
            // Create a temporary eshop for testing
            const tempEshopData = {
                ...eshopData,
                config_json: buildFullScraperConfig(scraperConfig, authConfig, advancedSettings, categories)
            };

            // We'll create a temporary eshop just for testing
            api.post('/api/eshops', tempEshopData)
                .then(response => {
                    const tempEshopId = response.data.id;
                    testProductLoadingMutation.mutate({
                        eshopId: effectiveEshopId || tempEshopId,
                        config: tempEshopData.config_json
                    });

                    // Clean up the temporary eshop after testing
                    setTimeout(() => {
                        api.delete(`/api/eshops/${tempEshopId}`).catch(() => {
                            // Ignore cleanup errors
                        });
                    }, 5000);
                })
                .catch(error => {
                    setTestProductLoadingResult({
                        success: false,
                        message: extractErrorMessage(error, 'Failed to create temporary e-shop for testing')
                    });
                    setIsTestingProductLoading(false);
                });
        } else {
            // For existing e-shops, test directly
            const config = buildFullScraperConfig(scraperConfig, authConfig, advancedSettings, categories);
            testProductLoadingMutation.mutate({ eshopId: effectiveEshopId, config });
        }
    };

    const handleOpenPreview = () => {
        // This function will be passed down to the SelectorConfigStep
        // The actual modal state is managed in the SelectorConfigStep
    };

    const [isAnalysisInProgress, setIsAnalysisInProgress] = useState(false);

    const handleAnalyzeAndSuggest = async () => {
        // Prevent multiple concurrent analyses
        if (isAnalysisInProgress) {
            setTestProductLoadingResult({
                success: false,
                message: 'Analysis is already in progress. Please wait for the current analysis to complete.'
            });
            return;
        }

        const urlToUse = scraperConfig.catalog_url || scraperConfig.url || eshopData.base_url;
        if (!urlToUse) {
            setTestProductLoadingResult({
                success: false,
                message: 'Please enter an E-shop URL in Step 1 first'
            });
            return;
        }

        setIsAnalysisInProgress(true);
        setIsAnalyzingSelectors(true);
        setTestProductLoadingResult(null);

        const maxIterations = scraperConfig.iterations || 1;
        let htmlContent = null;
        let testEshopId = effectiveEshopId;
        let tempEshopCreated = false;

        try {
            // Fetch HTML content from the URL with authentication if configured (only once)
            const fetchPayload = {
                url: urlToUse,
                auth_config: {
                    login_url: authConfig.login_url,
                    username_field: authConfig.username_field,
                    password_field: authConfig.password_field,
                    login_button: authConfig.login_button,
                    modal_container: authConfig.modal_container,
                    test_credentials: authConfig.test_credentials
                }
            };

            const htmlResponse = await api.post('/api/selectors/fetch-html', fetchPayload);

            if (!htmlResponse.data?.html_content) {
                throw new Error('Failed to fetch HTML content from the URL');
            }

            htmlContent = htmlResponse.data.html_content;

            // Log if authentication was used
            if (htmlResponse.data?.authenticated) {
                console.log('HTML fetched with authentication');
            }

            // Show warning if Selenium is not available but authentication was requested
            if (htmlResponse.data?.warning) {
                console.warn('Selenium warning:', htmlResponse.data.warning);
                setTestProductLoadingResult({
                    success: false,
                    message: htmlResponse.data.warning
                });
                setIsAnalyzingSelectors(false);
                return;
            }

            // Iterative analysis with validation
            for (let iteration = 1; iteration <= maxIterations; iteration++) {
                console.log(`Starting iteration ${iteration}/${maxIterations}`);

                setTestProductLoadingResult({
                    success: true,
                    message: `Iteration ${iteration}/${maxIterations}: Analyzing selectors...`
                });

                // Use the suggest endpoint which uses LLM with configurable HTML size
                // Add rate limiting with exponential backoff for API calls
                let suggestionResponse = null;
                let retryCount = 0;
                const maxRetries = 3;
                let baseDelay = 1000; // 1 second base delay

                while (retryCount < maxRetries) {
                    try {
                        suggestionResponse = await api.post(`/api/selectors/suggest?html_size=${htmlSize}`, {
                            html_content: htmlContent,
                            current_config: {
                                selectors: scraperConfig.selectors,
                                url: scraperConfig.url,
                                catalog_url: scraperConfig.catalog_url
                            }
                        });
                        break; // Success, exit retry loop
                    } catch (error) {
                        if (error.response?.status === 429) {
                            retryCount++;
                            if (retryCount >= maxRetries) {
                                throw new Error('Rate limit exceeded and maximum retries reached. Please try again later.');
                            }

                            // Calculate delay with exponential backoff and jitter
                            const jitter = Math.random() * 1000; // Add 0-1 second random jitter
                            const delay = Math.pow(2, retryCount - 1) * baseDelay + jitter;
                            console.log(`Rate limited, waiting ${Math.round(delay)}ms before retry ${retryCount}/${maxRetries}`);
                            setTestProductLoadingResult({
                                success: false,
                                message: `Rate limited, waiting before retry ${retryCount}/${maxRetries}...`
                            });
                            await new Promise(resolve => setTimeout(resolve, delay));
                        } else {
                            // Not a 429 error, re-throw
                            throw error;
                        }
                    }
                }

                // Add delay between iterations to prevent overwhelming the API
                if (iteration < maxIterations) {
                    console.log(`Waiting 2 seconds before iteration ${iteration + 1}...`);
                    await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay between iterations
                }

                if (!suggestionResponse.data?.selectors || Object.keys(suggestionResponse.data.selectors).length === 0) {
                    setTestProductLoadingResult({
                        success: false,
                        message: suggestionResponse.data?.message || 'No selectors could be identified. Please try entering them manually or use a different URL.'
                    });
                    break;
                }

                // Merge suggested selectors with existing ones, ensuring values are strings
                const newSelectors = {
                    ...scraperConfig.selectors,
                };

                // Process each suggested selector to ensure it's properly formatted
                Object.entries(suggestionResponse.data.selectors).forEach(([key, value]) => {
                    if (typeof value === 'object' && value !== null && value.selector) {
                        // Value is already in the new format { selector: '...', pageType: '...' }
                        newSelectors[key] = {
                            selector: typeof value.selector === 'string' || typeof value.selector === 'number'
                                ? String(value.selector)
                                : '',
                            pageType: value.pageType || 'list'
                        };
                    } else {
                        // Value is a string or other format
                        newSelectors[key] = {
                            selector: typeof value === 'string' || typeof value === 'number'
                                ? String(value)
                                : '',
                            pageType: 'list' // Default to list page
                        };
                    }
                });

                const newConfig = {
                    ...scraperConfig,
                    selectors: newSelectors,
                    catalog_url: scraperConfig.catalog_url,
                    url: scraperConfig.url
                };

                setScraperConfig(newConfig);

                // Show intermediate message
                const selectorCount = Object.keys(suggestionResponse.data.selectors).length;
                setTestProductLoadingResult({
                    success: true,
                    message: `Iteration ${iteration}/${maxIterations}: Applied ${selectorCount} selector(s). Testing...`
                });

                // Automatically test the selectors
                setIsTestingProductLoading(true);
                try {
                    // Prepare config for testing
                    const testConfig = buildFullScraperConfig(newConfig, authConfig, advancedSettings, categories);

                    // For new e-shops, create a temporary one for testing (only once)
                    if (!effectiveEshopId && !tempEshopCreated) {
                        const tempEshopData = {
                            ...eshopData,
                            config_json: testConfig
                        };

                        const tempResponse = await api.post('/api/eshops', tempEshopData);
                        testEshopId = tempResponse.data.id;
                        tempEshopCreated = true;

                        // Schedule cleanup
                        setTimeout(() => {
                            api.delete(`/api/eshops/${testEshopId}`).catch(() => {
                                // Ignore cleanup errors
                            });
                        }, 10000); // Longer timeout to allow for multiple iterations
                    }

                    // Test the product loading
                    const testResult = await testProductLoadingMutation.mutateAsync({
                        eshopId: testEshopId,
                        config: testConfig
                    });

                    // Check if products were found
                    const productCount = testResult.data?.products?.length || 0;

                    if (testResult.data?.success && productCount > 0) {
                        // Success! Products found
                        setTestProductLoadingResult({
                            success: true,
                            message: `Success on iteration ${iteration}! Applied ${selectorCount} selector(s) and found ${productCount} product(s)`,
                            products: testResult.data?.products
                        });
                        setIsTestingProductLoading(false);
                        break; // Exit the loop
                    } else {
                        // No products found, continue to next iteration if available
                        if (iteration < maxIterations) {
                            console.log(`Iteration ${iteration} found 0 products, trying iteration ${iteration + 1}...`);
                            setTestProductLoadingResult({
                                success: false,
                                message: `Iteration ${iteration}/${maxIterations}: Found 0 products. Trying again with different approach...`
                            });

                            // Add a delay between iterations to prevent overwhelming the API
                            await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
                            // Continue to next iteration
                        } else {
                            // Last iteration and still no products
                            setTestProductLoadingResult({
                                success: false,
                                message: `Completed ${maxIterations} iterations but found 0 products - manual adjustment needed`,
                                products: []
                            });
                        }
                    }
                } catch (testError) {
                    console.error('Validation error:', testError);
                    const errorMessage = extractErrorMessage(testError, 'Validation failed');

                    if (iteration < maxIterations) {
                        console.log(`Iteration ${iteration} validation failed, trying iteration ${iteration + 1}...`);
                        setTestProductLoadingResult({
                            success: false,
                            message: `Iteration ${iteration}/${maxIterations}: Validation failed. Trying again...`
                        });

                        // Add a delay between iterations to prevent overwhelming the API
                        await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
                        // Continue to next iteration
                    } else {
                        setTestProductLoadingResult({
                            success: false,
                            message: `Completed ${maxIterations} iterations but validation failed: ${errorMessage}`
                        });
                    }
                } finally {
                    setIsTestingProductLoading(false);
                }
            }
        } catch (error) {
            console.error('Analysis error:', error);
            const errorMessage = extractErrorMessage(error, 'Analysis failed');

            setTestProductLoadingResult({
                success: false,
                message: `Analysis failed: ${errorMessage}. Please check the URL and try again.`
            });
        } finally {
            setIsAnalyzingSelectors(false);
            setIsTestingProductLoading(false);
            setIsAnalysisInProgress(false);
        }
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return <EshopInfoStep
                    eshopData={eshopData}
                    setEshopData={setEshopData}
                    scraperConfig={scraperConfig}
                    setScraperConfig={setScraperConfig}
                    eshop={eshop}
                />;
            case 1:
                return <AuthSetupStep
                    ref={authSetupStepRef}
                    authConfig={authConfig}
                    setAuthConfig={setAuthConfig}
                    analyzeLoginMutation={analyzeLoginMutation}
                    testConnectionMutation={testConnectionMutation}
                    testConnectionResult={testConnectionResult}
                    analyzeLoginResult={analyzeLoginResult}
                    isTestingConnection={isTestingConnection}
                    isAnalyzingLogin={isAnalyzingLogin}
                    eshopId={effectiveEshopId}
                    htmlSize={htmlSize}
                    setHtmlSize={setHtmlSize}
                    iterations={iterations}
                    setIterations={setIterations}
                />;
            case 2:
                console.debug('[EshopWizard] rendering SelectorConfigStep', {
                    mountId: wizardMountIdRef.current,
                    hasTestResult: Boolean(testProductLoadingResult),
                    lastResultHash: testProductLoadingResult ? JSON.stringify({
                        success: testProductLoadingResult.success,
                        message: testProductLoadingResult.message,
                        productsCount: testProductLoadingResult.products?.length || 0,
                        firstProductName: testProductLoadingResult.products?.[0]?.name || testProductLoadingResult.products?.[0]?.product_name || ''
                    }) : null,
                    timestamp: Date.now()
                });
                return <SelectorConfigStep
                    ref={selectorConfigStepRef}
                    scraperConfig={scraperConfig}
                    setScraperConfig={setScraperConfig}
                    eshopData={eshopData}
                    testProductLoadingMutation={testProductLoadingMutation}
                    testProductLoadingResult={testProductLoadingResult}
                    isTestingProductLoading={isTestingProductLoading}
                    handleTestProductLoading={handleTestProductLoading}
                    handleAnalyzeAndSuggest={handleAnalyzeAndSuggest}
                    categories={categories}
                    setCategories={setCategories}
                />;
            case 3:
                return <AdvancedSettingsStep
                    advancedSettings={advancedSettings}
                    setAdvancedSettings={setAdvancedSettings}
                />;
            case 4:
                return <ReviewStep
                    eshopData={eshopData}
                    scraperConfig={scraperConfig}
                    authConfig={authConfig}
                    advancedSettings={advancedSettings}
                    categories={categories}
                />;
            default:
                return 'Unknown step';
        }
    };

    if (isEshopLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ py: 1 }}>
                {steps.map((label, index) => (
                    <Step key={label}>
                        <StepLabel
                            sx={{
                                fontSize: '0.75rem',
                                cursor: effectiveEshopId ? 'pointer' : 'default',
                                '&:hover': effectiveEshopId ? { opacity: 0.7 } : {}
                            }}
                            onClick={() => {
                                if (effectiveEshopId) {
                                    jumpToStep(index);
                                }
                            }}
                        >
                            {label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {validationError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {validationError}
                </Alert>
            )}

            <Card sx={{ mt: 2 }}>
                <CardContent sx={{ p: 2 }}>
                    {getStepContent(activeStep)}
                </CardContent>
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, alignItems: 'center' }}>
                {/* Action buttons for each step */}
                {activeStep === 1 && (
                    <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                        <Button
                            variant="contained"
                            onClick={() => {
                                // Check if analysis is still running and wait for it to complete
                                if (isAnalyzingLogin) {
                                    setTestConnectionResult({
                                        success: false,
                                        message: 'Login analysis is still in progress. Please wait for it to complete before testing the connection.'
                                    });
                                    setIsTestingConnection(false);
                                    return;
                                }

                                // For new e-shops, we need to create a temporary one first
                                if (!effectiveEshopId) {
                                    // Check if auth config has been set up properly with more robust validation
                                    if (!authConfig.login_url?.trim() || !authConfig.username_field?.trim() || !authConfig.password_field?.trim() || !authConfig.login_button?.trim()) {
                                        setTestConnectionResult({
                                            success: false,
                                            message: 'Authentication configuration is incomplete. Please run "Analyze Login" first or fill in all authentication fields manually. All fields must contain valid CSS selectors.'
                                        });
                                        setIsTestingConnection(false);
                                        return;
                                    }

                                    // Additional check: ensure test credentials are provided
                                    if (!authConfig.test_credentials?.username?.trim() || !authConfig.test_credentials?.password?.trim()) {
                                        setTestConnectionResult({
                                            success: false,
                                            message: 'Test credentials are required for authentication testing. Please enter a test username and password.'
                                        });
                                        setIsTestingConnection(false);
                                        return;
                                    }

                                    // Create a temporary eshop for testing
                                    const tempEshopData = {
                                        ...eshopData,
                                        config_json: buildFullScraperConfig(scraperConfig, authConfig, advancedSettings, categories)
                                    };

                                    // Directly call the test endpoint with the config, without creating a temporary e-shop
                                    // This avoids the issue of the temporary e-shop being deleted before the test completes
                                    testConnectionMutation.mutate({
                                        eshopId: effectiveEshopId || 'temp', // Use actual eshopId if available, otherwise temp
                                        config: tempEshopData.config_json
                                    });
                                } else {
                                    // For existing e-shops, test directly
                                    // Check if auth config has been set up properly with more robust validation
                                    if (!authConfig.login_url?.trim() || !authConfig.username_field?.trim() || !authConfig.password_field?.trim() || !authConfig.login_button?.trim()) {
                                        setTestConnectionResult({
                                            success: false,
                                            message: 'Authentication configuration is incomplete. Please run "Analyze Login" first or fill in all authentication fields manually. All fields must contain valid CSS selectors.'
                                        });
                                        setIsTestingConnection(false);
                                        return;
                                    }

                                    // Additional check: ensure test credentials are provided
                                    if (!authConfig.test_credentials?.username?.trim() || !authConfig.test_credentials?.password?.trim()) {
                                        setTestConnectionResult({
                                            success: false,
                                            message: 'Test credentials are required for authentication testing. Please enter a test username and password.'
                                        });
                                        setIsTestingConnection(false);
                                        return;
                                    }

                                    const config = buildFullScraperConfig(scraperConfig, authConfig, advancedSettings, categories);
                                    testConnectionMutation.mutate({ eshopId: effectiveEshopId, config });
                                }
                            }}
                            disabled={isTestingConnection || isAnalyzingLogin}
                            startIcon={isTestingConnection ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                        >
                            {isTestingConnection ? 'Testing...' : 'Test Connection'}
                        </Button>
                        <Button
                            variant="outlined"
                            color='primary'
                            onClick={() => {
                                if (authSetupStepRef.current && typeof authSetupStepRef.current.handleOpenPreview === 'function') {
                                    authSetupStepRef.current.handleOpenPreview();
                                } else {
                                    // Fallback: show an alert if the ref function is not available
                                    alert('The preview modal will automatically open when test results are available. Run a test to see results.');
                                }
                            }}
                            disabled={!testConnectionResult}
                            startIcon={<ArticleIcon />}
                        >
                            Show Log
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                if (!authConfig.login_url) {
                                    setAnalyzeLoginResult({
                                        success: false,
                                        message: 'Please enter a Login URL first'
                                    });
                                    return;
                                }

                                analyzeLoginMutation.mutate({
                                    loginUrl: authConfig.login_url,
                                    iterations: iterations,
                                    htmlSize: htmlSize
                                });
                            }}
                            disabled={!authConfig.login_url || isTestingConnection || isAnalyzingLogin}
                            startIcon={isAnalyzingLogin ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                        >
                            {isAnalyzingLogin ? 'Analyzing...' : 'Analyze Login'}
                        </Button>

                        <TextField
                            type="number"
                            label="HTML Size (chars to LLM)"
                            value={htmlSize}
                            onChange={(e) => setHtmlSize(Math.max(0, Math.min(50000, parseInt(e.target.value) || 0)))}
                            size="small"
                            sx={{ width: 200 }}
                            inputProps={{ min: 0, max: 50000, step: 1000 }}
                        />
                        <TextField
                            type="number"
                            label="Iterations (1-10)"
                            value={iterations}
                            onChange={(e) => setIterations(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                            size="small"
                            sx={{ width: 140 }}
                            inputProps={{ min: 1, max: 10 }}
                        />
                    </Box>
                )}

                {activeStep === 2 && (
                    <Box sx={{ display: 'flex', gap: 1, mr: 2, alignItems: 'flex-start', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={handleTestProductLoading}
                                disabled={isTestingProductLoading || isAnalyzingSelectors}
                                startIcon={isTestingProductLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                            >
                                {isTestingProductLoading ? 'Testing...' : 'Test Product Loading'}
                            </Button>
                            <Button
                                variant="outlined"
                                color='primary'
                                onClick={() => {
                                    if (selectorConfigStepRef.current && typeof selectorConfigStepRef.current.handleOpenPreview === 'function') {
                                        selectorConfigStepRef.current.handleOpenPreview();
                                    } else {
                                        // Fallback: show an alert if the ref function is not available
                                        alert('The preview modal will automatically open when test results are available. Run a test to see results.');
                                    }
                                }}
                                startIcon={<ArticleIcon />}
                            >
                                Show Log
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleAnalyzeAndSuggest}
                                disabled={isTestingProductLoading || isAnalyzingSelectors}
                                startIcon={isAnalyzingSelectors ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                            >
                                {isAnalyzingSelectors ? 'Analyzing...' : 'Analyze and suggest'}
                            </Button>
                            <TextField
                                type="number"
                                label="HTML Size (chars to LLM)"
                                value={htmlSize}
                                onChange={(e) => setHtmlSize(Math.max(0, Math.min(50000, parseInt(e.target.value) || 0)))}
                                size="small"
                                sx={{ width: 200 }}
                                inputProps={{ min: 0, max: 50000, step: 1000 }}
                            />
                            <TextField
                                type="number"
                                label="Iterations (1-5)"
                                value={scraperConfig.iterations || 1}
                                onChange={(e) => setScraperConfig({ ...scraperConfig, iterations: parseInt(e.target.value) || 1 })}
                                size="small"
                                sx={{ width: 140 }}
                                inputProps={{ min: 1, max: 5 }}
                            />
                        </Box>

                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 4, ml: 'auto' }}>
                    {/* Save button for edit mode - appears on all steps when editing */}
                    {effectiveEshopId && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                // For edit mode, allow saving at any step
                                if (effectiveEshopId) {
                                    const fullConfig = buildFullScraperConfig(scraperConfig, authConfig, advancedSettings, categories);

                                    saveEshopMutation.mutate({
                                        id: effectiveEshopId,
                                        ...eshopData,
                                        config_json: fullConfig
                                    });
                                }
                            }}
                            disabled={createEshopMutation.isLoading || updateEshopMutation.isLoading || saveEshopMutation.isLoading}
                            startIcon={<SaveIcon />}
                        >
                            Save
                        </Button>
                    )}

                    {/* Navigation buttons */}
                    <Button
                        variant="contained"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        startIcon={<BackIcon />}
                    >
                        Back
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => handleNext(scraperConfig)}
                        disabled={createEshopMutation.isLoading || updateEshopMutation.isLoading}
                        startIcon={
                            activeStep === steps.length - 1 ?
                                (createEshopMutation.isLoading || updateEshopMutation.isLoading ?
                                    <CircularProgress size={16} /> :
                                    <SaveIcon />
                                ) :
                                <ForwardIcon />
                        }
                    >
                        {activeStep === steps.length - 1 ?
                            (createEshopMutation.isLoading || updateEshopMutation.isLoading ?
                                'Processing...' :
                                'Save E-shop'
                            ) :
                            'Next'}
                    </Button>
                </Box>
            </Box>
            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default EshopWizard;